import React, { useState, useEffect, useCallback } from "react";
import TablePagination from "@mui/material/TablePagination";
import { useLocation } from "react-router-dom";
import TableContainer from "@mui/material/TableContainer";
import { apiCall } from "../../services/authServieces";
import ShowSnackBar from "../../components/snackBar";
import SearchIcon from "@mui/icons-material/Search";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import "../../css/manage_waba.css";
import "../../css/wb_report_details.css";
import { formatDate } from "../../utils/helper";
import { ClipLoader } from "react-spinners";

const TemplateReportDetail = () => {
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [snackBar, setSnackBar] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const location = useLocation();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const rowData = location.state?.rowData;
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [checkedRows, setCheckedRows] = useState({});

  const columns = [
    { id: "temp_id", label: "Template Id", minWidth: 100 },
    { id: "temp_name", label: "Template Name", minWidth: 100 },
    { id: "t_type", label: "Template Type", minWidth: 100 },
    { id: "cat", label: "Category", minWidth: 100 },
    { id: "lang", label: "Language", minWidth: 100 },
    { id: "msg_var_count", label: "Variable Count", minWidth: 100 },
    { id: "managed_by_oem", label: "Action", minWidth: 100 },
  ];

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleCloseSnackBar = (event, reason) => {
    setSnackBar((prevState) => ({ ...prevState, open: false }));
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      if (rowData?.user_id) {
        let endpoint = `templates/get-oem-template-details/${rowData?.user_id}?page=${page}&limit=${rowsPerPage}`;
        if (debouncedSearchTerm) {
          endpoint = `${endpoint}&search=${debouncedSearchTerm}`;
        }
        const response = await apiCall({ method: "GET", endpoint: endpoint });
        if (response.success) {
          setRows(response.data);
          setTotal(response.total);

          // Initialize checkedRows based on `managed_by_oem` value
          const initialCheckedRows = {};
          response.data.forEach((row) => {
            if (row.managed_by_oem === 'TRUE') {
              initialCheckedRows[row.id] = true; // Pre-check if managed_by_oem is 'TRUE'
            }
          });
          setCheckedRows(initialCheckedRows); // Update checked state
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, rowData?.created_by, debouncedSearchTerm]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCheckboxChange = async (event, row) => {
    const { checked } = event.target;
    setCheckedRows((prevCheckedRows) => ({
      ...prevCheckedRows,
      [row.id]: checked,
    }));

    try {
      const endpoint = `templates/update-status/${row.id}`;
      const data = { status: checked ? "TRUE" : 'NULL' };

      const response = await apiCall({
        method: "POST",
        endpoint: endpoint,
        payload: data,
      });

      if (response.success) {
        setSnackBar({
          open: true,
          severity: "success",
          message: "Status updated successfully",
        });
      } else {
        setSnackBar({
          open: true,
          severity: "error",
          message: response.message || "Failed to update status",
        });
      }
    } catch (error) {
      console.error(error);
      setSnackBar({
        open: true,
        severity: "error",
        message: "An error occurred",
      });
    }
  };

  return (
    <>
      {loading && (
        <div className="spinnerOverlay">
          <ClipLoader loading={loading} size={50} />
        </div>
      )}
      <div className="Template_id_contian">
        <h4 className="Head_title ReportDetails">
          <span>{rowData?.user_name}</span>
          Templates
          <div className="Session_report_SearchContain">
            <input
              placeholder="Search"
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <button type="button">
              <SearchIcon />
            </button>
          </div>
        </h4>
        <Paper sx={{ width: "100%", overflow: "hidden" }}>
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader aria-label="sticky table" className="Table DeliveryReapotTable">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        style={{ minWidth: column.minWidth }}
                      >
                        {column.id === "managed_by_oem" ? (
                          <input
                            type="checkbox"
                            checked={checkedRows[row.id] || row.managed_by_oem === 'TRUE'}
                            onChange={(e) => handleCheckboxChange(e, row)}
                          />
                        ) : (
                          row[column.id]
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </div>
      <ShowSnackBar
        open={snackBar.open}
        severity={snackBar.severity}
        message={snackBar.message}
        onClose={handleCloseSnackBar}
      />
    </>
  );
};



export default TemplateReportDetail;
